import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, Typography, Box, Alert,
  Divider, Chip
} from '@mui/material';
import { Close, WhatsApp, Add, Delete } from '@mui/icons-material';
import { apiRequest } from '../../fetchSetup';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER

const emptyItem = () => ({ medicine_name: '', notes: '' });

const MedicineRequestModal = ({ open, onClose, prefilledName = '', userEmail = '', userPhone = '' }) => {
  const [items, setItems] = useState([{ medicine_name: prefilledName, notes: '' }]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // ── item helpers ──────────────────────────────────────────────────────────
  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);

  const removeItem = (index) => {
    if (items.length === 1) return; // keep at least one row
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // ── submit ────────────────────────────────────────────────────────────────

const handleSubmit = async () => {
  const valid = items.filter(i => i.medicine_name.trim());
  if (valid.length === 0) { setError('Please enter at least one medicine name.'); return; }
  setLoading(true); setError('');

  try {
    // Send ALL medicines in ONE request
    const res = await apiRequest(
      `${process.env.REACT_APP_MEDICINE_API_URL}/medicine-requests`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          medicines: valid.map(i => ({
            medicine_name: i.medicine_name.trim(),
            notes: i.notes,
          })),
          user_email: userEmail,
          user_phone: userPhone,
        }),
      }
    );
    if (!res.ok) throw new Error('Failed to submit');
    setDone(true);
  } catch (e) {
    setError('Could not submit. Please try WhatsApp instead.');
  } finally {
    setLoading(false);
  }
};






  // ── whatsapp ──────────────────────────────────────────────────────────────
  const handleWhatsApp = () => {
    const filled = items.filter(i => i.medicine_name.trim());
    const list = filled.length > 0
      ? filled.map((i, idx) => `${idx + 1}. *${i.medicine_name}*${i.notes ? ` (${i.notes})` : ''}`).join('\n')
      : '(no medicines entered)';
    const text = encodeURIComponent(`Hi, I'd like to request medicines not listed in the app:\n${list}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
  };

  // ── close / reset ─────────────────────────────────────────────────────────
  const handleClose = () => {
    setDone(false);
    setError('');
    setItems([{ medicine_name: prefilledName, notes: '' }]);
    onClose();
  };

  const filledCount = items.filter(i => i.medicine_name.trim()).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography fontWeight={700} fontSize={18}>Request medicines</Typography>
          {!done && items.length > 1 && (
            <Typography fontSize={12} color="text.secondary">
              {items.length} medicines added
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {filledCount === 1
              ? 'Request submitted! Our team will add it to the catalog soon.'
              : `${filledCount} requests submitted! Our team will review and add them soon.`}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {items.map((item, index) => (
              <Box key={index}>
                {index > 0 && <Divider sx={{ mb: 2 }} />}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Chip
                    label={`Medicine ${index + 1}`}
                    size="small"
                    sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: 11 }}
                  />
                  {items.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeItem(index)}
                      sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Medicine name *"
                    value={item.medicine_name}
                    onChange={(e) => updateItem(index, 'medicine_name', e.target.value)}
                    fullWidth
                    autoFocus={index === 0}
                    size="small"
                    placeholder="e.g. Paracetamol 500mg"
                  />
                  <TextField
                    label="Notes (optional)"
                    value={item.notes}
                    onChange={(e) => updateItem(index, 'notes', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. dosage, brand preference"
                  />
                </Box>
              </Box>
            ))}

            {/* Add another medicine button */}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addItem}
              size="small"
              sx={{
                textTransform: 'none',
                borderStyle: 'dashed',
                borderColor: '#006d77',
                color: '#006d77',
                fontWeight: 600,
                '&:hover': { bgcolor: '#f0fdfa', borderStyle: 'dashed' }
              }}
            >
              Add another medicine
            </Button>
          </Box>
        )}
      </DialogContent>

      {!done && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<WhatsApp />}
            onClick={handleWhatsApp}
            sx={{
              borderColor: '#25D366',
              color: '#25D366',
              textTransform: 'none',
              '&:hover': { borderColor: '#1ebe57', bgcolor: '#f0fdf4' }
            }}
          >
            WhatsApp us
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              bgcolor: '#006d77',
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              '&:hover': { bgcolor: '#005560' }
            }}
          >
            {loading
              ? 'Submitting…'
              : filledCount > 1
                ? `Submit ${filledCount} requests`
                : 'Submit request'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default MedicineRequestModal;